
import Link from '@/components/compat/Link';
import { useMemo, useState } from 'react';
import { BookOpen, Check, ExternalLink, Pencil, X, Zap } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { AceNavIcon } from '@/components/help-center/aces/AceIcon';
import { Button } from '@/components/ui/button';
import {
  canAccessReviewQueue,
  PUBLICATION_STATUS_BADGE_CLASS,
  PUBLICATION_STATUS_LABELS,
} from '@/lib/cms-publication';
import {
  painelButtonOutlineClass,
  painelButtonPrimaryClass,
  painelTableShellClass,
} from '@/components/painel/painel-styles';
import { useCms } from '@/components/painel/cms/cms-context';
import { usePowersCms } from '@/components/painel/powers/powers-cms-context';
import { useAcesCms } from '@/components/painel/aces/aces-cms-context';
import { usePainelSession } from '@/components/painel/rbac/painel-session-context';
import { usePainelSaveFeedback } from '@/components/painel/PainelSaveFeedback';
import { logPainelApproveReview } from '@/lib/painel-activity-log';
import { cn } from '@/lib/utils';

type ReviewTab = 'guias' | 'powers' | 'aces';

type ReviewItem = {
  id: string;
  type: ReviewTab;
  title: string;
  subtitle?: string;
  updatedAt: string;
  author?: string;
  editHref: string;
  previewHref?: string;
};

const TAB_CONFIG: { id: ReviewTab; label: string; icon: LucideIcon; empty: string }[] = [
  { id: 'guias', label: 'Guias', icon: BookOpen, empty: 'Nenhum guia em revisão no momento.' },
  { id: 'powers', label: 'Powers', icon: Zap, empty: 'Nenhum power em revisão no momento.' },
  { id: 'aces', label: 'Aces', icon: AceNavIcon, empty: 'Nenhum ace em revisão no momento.' },
];

function formatUpdatedAt(iso: string) {
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function ReviewTable({
  items,
  hydrated,
  emptyMessage,
  onApprove,
  onReject,
}: {
  items: ReviewItem[];
  hydrated: boolean;
  emptyMessage: string;
  onApprove: (item: ReviewItem) => void;
  onReject: (item: ReviewItem) => void;
}) {
  return (
    <div className={painelTableShellClass}>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] border-collapse text-left font-inter text-sm">
          <thead>
            <tr className="border-b border-white/10 bg-white/[0.04]">
              <th className="px-4 py-3 font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/70">
                Título
              </th>
              <th className="px-4 py-3 font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/70">
                Estado
              </th>
              <th className="px-4 py-3 font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/70">
                Enviado
              </th>
              <th className="px-4 py-3 text-right font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/70">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/[0.06]">
            {!hydrated ? (
              <tr>
                <td colSpan={4} className="px-4 py-10 text-center text-slate-400">
                  Carregando…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-12 text-center text-slate-400">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id} className="transition-colors hover:bg-white/[0.03]">
                  <td className="px-4 py-3">
                    <div className="font-poppins font-medium text-blue-50">{item.title}</div>
                    {item.subtitle ? (
                      <div className="mt-0.5 text-xs text-slate-400">{item.subtitle}</div>
                    ) : null}
                    {item.author ? (
                      <div className="mt-0.5 text-xs text-slate-500">por {item.author}</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={cn(
                        'inline-flex rounded-full px-2.5 py-0.5 font-poppins text-[11px] font-semibold uppercase tracking-wide ring-1',
                        PUBLICATION_STATUS_BADGE_CLASS.pending_review
                      )}
                    >
                      {PUBLICATION_STATUS_LABELS.pending_review}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-300">{formatUpdatedAt(item.updatedAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      {item.previewHref ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className={cn(painelButtonOutlineClass, 'h-8')}
                          asChild
                        >
                          <a href={item.previewHref} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                            Pré-ver
                          </a>
                        </Button>
                      ) : null}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className={cn(painelButtonOutlineClass, 'h-8')}
                        asChild
                      >
                        <Link href={item.editHref}>
                          <Pencil className="mr-1.5 h-3.5 w-3.5" />
                          Editar
                        </Link>
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        className={cn(painelButtonPrimaryClass, 'h-8')}
                        onClick={() => onApprove(item)}
                      >
                        <Check className="mr-1.5 h-3.5 w-3.5" />
                        Aprovar
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 rounded-xl border-rose-400/30 bg-rose-500/10 text-rose-100 hover:bg-rose-500/20"
                        onClick={() => onReject(item)}
                      >
                        <X className="mr-1.5 h-3.5 w-3.5" />
                        Rejeitar
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function PainelRevisaoPage() {
  const { session, role } = usePainelSession();
  const { showSaveSuccess } = usePainelSaveFeedback();
  const { articles, hydrated: cmsHydrated, updateArticle, getCategoryById } = useCms();
  const { powers, hydrated: powersHydrated, setPowerPublicationStatus } = usePowersCms();
  const { aces, hydrated: acesHydrated, setAcePublicationStatus } = useAcesCms();
  const [activeTab, setActiveTab] = useState<ReviewTab>('guias');

  const canReview = role ? canAccessReviewQueue(role) : false;
  const hydrated = cmsHydrated && powersHydrated && acesHydrated;

  const pendingByTab = useMemo(() => {
    if (!hydrated) {
      return { guias: [], powers: [], aces: [] } satisfies Record<ReviewTab, ReviewItem[]>;
    }

    const guias: ReviewItem[] = articles
      .filter((a) => a.status === 'pending_review')
      .map((a) => ({
        id: `guia:${a.id}`,
        type: 'guias' as const,
        title: a.title,
        subtitle: getCategoryById(a.categoryId)?.name,
        updatedAt: a.updatedAt,
        author: a.updatedByName,
        editHref: `/painel/guias/${a.id}/edit`,
      }))
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

    const powerItems: ReviewItem[] = powers
      .filter((p) => p.publicationStatus === 'pending_review')
      .map((p) => ({
        id: `power:${p.slug}`,
        type: 'powers' as const,
        title: p.name,
        subtitle: p.slug,
        updatedAt: p.updatedAt,
        editHref: `/painel/powers/${encodeURIComponent(p.slug)}/edit`,
        previewHref: `/powers/${p.slug}`,
      }))
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

    const aceItems: ReviewItem[] = aces
      .filter((a) => a.publicationStatus === 'pending_review')
      .map((a) => ({
        id: `ace:${a.slug}`,
        type: 'aces' as const,
        title: a.name,
        subtitle: a.slug,
        updatedAt: a.updatedAt,
        editHref: `/painel/aces/${encodeURIComponent(a.slug)}/edit`,
        previewHref: `/aces/${a.slug}`,
      }))
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

    return { guias, powers: powerItems, aces: aceItems };
  }, [hydrated, articles, powers, aces, getCategoryById]);

  const activeConfig = TAB_CONFIG.find((t) => t.id === activeTab)!;
  const activeItems = pendingByTab[activeTab];

  const handleApprove = (item: ReviewItem) => {
    const resourceId = item.id.split(':')[1] ?? item.id;
    const resource =
      item.type === 'guias' ? ('guide' as const) : item.type === 'powers' ? ('power' as const) : ('ace' as const);

    if (item.type === 'guias') {
      updateArticle(resourceId, { status: 'published' });
    } else if (item.type === 'powers') {
      setPowerPublicationStatus(resourceId, 'published');
    } else {
      setAcePublicationStatus(resourceId, 'published');
    }

    logPainelApproveReview(session, {
      resource,
      resourceId,
      resourceLabel: item.title,
    });

    showSaveSuccess({
      title: 'Conteúdo aprovado',
      description: `“${item.title}” foi publicado no site.`,
    });
  };

  const handleReject = (item: ReviewItem) => {
    if (item.type === 'guias') {
      updateArticle(item.id.replace('guia:', ''), { status: 'draft' });
    } else if (item.type === 'powers') {
      setPowerPublicationStatus(item.id.replace('power:', ''), 'draft');
    } else {
      setAcePublicationStatus(item.id.replace('ace:', ''), 'draft');
    }
    showSaveSuccess({
      title: 'Devolvido ao editor',
      description: `“${item.title}” voltou a rascunho.`,
    });
  };

  if (!canReview) {
    return (
      <div className="space-y-4">
        <h1 className="font-poppins text-2xl font-bold text-white">Revisão</h1>
        <p className="font-inter text-sm text-slate-400">
          Não tem permissão para aceder à fila de revisão.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-poppins text-2xl font-bold tracking-tight text-white md:text-3xl">
          Revisão
        </h1>
        <p className="mt-1 max-w-2xl font-inter text-sm text-slate-400">
          Conteúdos enviados por editores aguardam aprovação antes de aparecer no site público.
        </p>
      </div>

      <div
        className="inline-flex rounded-full border border-white/10 bg-white/[0.04] p-1 backdrop-blur-sm"
        role="tablist"
        aria-label="Tipo de conteúdo em revisão"
      >
        {TAB_CONFIG.map((tab) => {
          const Icon = tab.icon;
          const count = pendingByTab[tab.id].length;
          const isActive = activeTab === tab.id;

          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'inline-flex items-center gap-2 rounded-full px-4 py-2 font-poppins text-sm font-semibold transition-all duration-200',
                isActive
                  ? 'bg-gradient-to-r from-sky-500 to-indigo-600 text-white shadow-md shadow-sky-950/40'
                  : 'text-slate-300 hover:bg-white/[0.06] hover:text-white'
              )}
            >
              <Icon className="h-4 w-4 shrink-0 opacity-90" />
              {tab.label}
              <span
                className={cn(
                  'inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums',
                  isActive ? 'bg-white/20 text-white' : 'bg-white/10 text-slate-300'
                )}
              >
                {hydrated ? count : '—'}
              </span>
            </button>
          );
        })}
      </div>

      <ReviewTable
        items={activeItems}
        hydrated={hydrated}
        emptyMessage={activeConfig.empty}
        onApprove={handleApprove}
        onReject={handleReject}
      />
    </div>
  );
}
